/**
 * The writer's bar and rail (decision 77; part 11f log, tables WT and WL): one slim bar (Library, the book and the open
 * chapter, the save state in words, the words, the layout, Find, Notes, History, Share, the Export menu and More), and
 * on a wide screen the chapter rail, collapsed to the chapters' numbers or open with the whole chapter list. On a narrow
 * screen the bar keeps Library, Chapters, the save state, Export and More, and the bottom switch shows the text or the
 * pages.
 */
import { createMemo, createSignal, mergeProps, untrack } from 'solid-js';
import { WRITER_LAYOUTS } from '../shell/settings.ts';
import { openFind, FindButton } from './find.ts';
import { el, when, whenSome } from '../ui/dom.ts';
import { ExportMenu } from './export-menu.ts';
import { NoteMarksToggle, NotesButton, openNotes } from './notes.ts';
import { ReimportItem } from './reimport.ts';
import { openWords, WordsButton } from './words.ts';
import { ChapterList, shownBook, shownTitle } from './chapter-list.ts';
import { InsertImage } from './images.ts';
import Badge from '../ui/Badge.ts';
import BottomBar from '../ui/BottomBar.ts';
import Button from '../ui/Button.ts';
import Divider from '../ui/Divider.ts';
import FormatToolbar from '../ui/FormatToolbar.ts';
import type { FormatTool } from '../ui/FormatToolbar.ts';
import IconButton from '../ui/IconButton.ts';
import Menu from '../ui/Menu.ts';
import type { MenuEntry, MenuTriggerProps } from '../ui/Menu.ts';
import SavedIndicator from '../ui/SavedIndicator.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import UiChapterList from '../ui/ChapterList.ts';
import type { Chapter } from '../ui/ChapterList.ts';
import { component } from '../ui/solid.ts';
import type { AutosaveStatus } from '@cockatrice/editor';
import type { Child } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** The open chapter's number among the live chapters and its title as shown; null with none open or not listed yet. */
function openPlace(shell: Shell): { readonly number: number; readonly title: string } | null {
  const { workspace } = shell;
  const chapters = workspace.listing()?.chapters ?? [];
  const index = chapters.findIndex((entry) => entry.id === workspace.chapterId());
  const entry = chapters[index];
  return entry === undefined ? null : { number: index + 1, title: shownTitle(shell, entry) };
}

/** WT3: the open chapter as the bar names it: its number and title; nothing with none open. */
export function chapterLine(shell: Shell): string {
  const place = openPlace(shell);
  return place === null ? '' : shell.t('writer.chapter', place);
}

/** The formatting the design's bar offers that the editor can't apply yet, each with the mark it shows (WT15). */
const LATER_FORMATS = [
  ['bold', 'writer.boldMark'],
  ['italic', 'writer.italicMark'],
  ['smallCaps', 'writer.smallCapsMark'],
  ['quote', 'writer.quoteMark'],
] as const;

/**
 * Part 11f: the head of the manuscript: the formatting bar (the library's FormatToolbar: the formats still to come, named
 * so, and Insert image, WT9, WT15) and the open chapter's number and title, as the design sets them over its text.
 */
export function ManuscriptHead(shell: Shell): Child {
  const { t } = shell;
  const place = (): { readonly number: number; readonly title: string } | null => openPlace(shell);
  return el(
    'div',
    { class: 'wr-head' },
    // Part 36a, RO: the design's read-only writer has no formatting bar.
    when(
      () => shell.account.permit(),
      () => {
        const image = InsertImage(shell);
        const later = LATER_FORMATS.map(([kind, mark]): FormatTool => ({
          // The library's ids, so each mark has its look (bold, italic, small caps).
          id: kind.toLowerCase(),
          glyph: t(mark),
          toggle: false,
          disabled: true,
          class: `fmt fmt-${kind}`,
          label: t('home.laterName', { name: t(`writer.${kind}`) }),
        }));
        return [
          component(FormatToolbar, {
            class: 'wr-format',
            label: t('writer.format'),
            tools: [...later, image.tool],
            onAction: () => {
              // The formats to come take no press: the picture is the one tool that acts.
              image.open();
            },
          }),
          image.picker,
        ];
      },
    ),
    whenSome(place, (now) => [el('p', { class: 'wr-kicker' }, () => t('writer.kicker', { number: now().number })), el('h2', { class: 'wr-chapter-heading' }, () => now().title)]),
  );
}

/** WT15: a control the design shows that isn't built yet: named as coming later, never pressed. */
function LaterIcon(shell: Shell, icon: 'history', name: string): Child {
  return component(IconButton, { icon, label: shell.t('home.laterName', { name }), class: 'later-icon', 'aria-disabled': 'true' });
}

/** A thin rule between the bar's groups, as the design's (the library's Divider, unread). */
function Rule(): Child {
  return component(Divider, { orientation: 'vertical', class: 'wr-sep', 'aria-hidden': 'true' });
}

/** WL1–WL3: the wide screen's layout, Side by side, Pages or Text (the library's SegmentedControl). */
function LayoutSwitch(shell: Shell): Child {
  const { t, workspace } = shell;
  return component(SegmentedControl, {
    label: t('writer.layout'),
    class: 'wr-layout',
    size: 'sm',
    options: [
      { value: 'side', label: t('writer.side') },
      { value: 'pages', label: t('writer.pages') },
      { value: 'text', label: t('writer.text') },
    ],
    get value() {
      return workspace.settings().writerLayout;
    },
    onChange: (value: string) => {
      for (const writerLayout of WRITER_LAYOUTS) if (writerLayout === value) void workspace.updateSettings({ writerLayout });
    },
  });
}

/**
 * WT5, WT7, WT8, WT11, WT13, WT15: More (the library's Menu, its button the library's IconButton): on a narrow screen first
 * what the bar has no room for (the words, Find, Notes, History and Share to come), then on every screen focus mode,
 * re-importing a Word file (its file picker beside the menu), the settings and the help.
 */
function MoreMenu(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  const reimport = ReimportItem(shell, { label: t('writer.reimport'), hint: t('writer.reimportHint') });
  // Made once each, so the menu keeps its items (and the focus on one) as the screen or the account changes.
  const narrowOnly: readonly MenuEntry[] = [
    { class: 'open-words', label: t('words.title'), dialog: true, onSelect: () => { openWords(shell); } },
    { class: 'open-find', label: t('toolbar.find'), onSelect: () => { openFind(shell); } },
    { class: 'open-notes-menu', label: t('notes.button'), dialog: true, onSelect: () => { openNotes(shell); } },
    { label: t('writer.history'), hint: t('home.later'), disabled: true },
  ];
  // Parts 38d, 38b: Share, where this build and account can share; else still to come.
  const share: MenuEntry = { class: 'open-share-menu', label: t('writer.share'), dialog: true, onSelect: () => { openShare(shell); } };
  const shareLater: MenuEntry = { label: t('writer.share'), hint: t('home.later'), disabled: true };
  const focus: MenuEntry = {
    label: t('toolbar.focus'),
    onSelect: () => {
      void workspace.updateSettings({ focusMode: true });
      // A24: the menu goes with the bar; the writer came for the text.
      shell.focusEditor();
    },
  };
  const always: readonly MenuEntry[] = [
    reimport.item,
    { separator: true },
    { class: 'open-settings', label: t('toolbar.settings'), onSelect: () => { shell.go('settings'); } },
    { class: 'open-help', label: t('toolbar.help'), dialog: true, onSelect: () => ui.setDialog({ kind: 'help', first: false }) },
  ];
  const separator: MenuEntry = { separator: true };
  return [
    component(Menu, {
      label: t('writer.more'),
      class: 'writer-more',
      get items() {
        // Decision 78: what works on the text stays with it; Formatting doesn't show the text.
        const writing = ui.route() !== 'format';
        const shared = shell.sharing.invitesAvailable() ? share : shareLater;
        const lead = shell.mode() === 'narrow' ? [...(writing ? narrowOnly : []), shared, separator] : [];
        return [...lead, ...(writing ? [focus] : []), ...always];
      },
      trigger: (props: MenuTriggerProps) => component(IconButton, mergeProps(props, { icon: 'more' as const, label: t('writer.more'), class: 'more-menu' })),
    }),
    reimport.picker,
  ];
}

/**
 * Part 38b: Share the open book with people, a reading link a press away there (part 38d's dialog). Signed in on a build
 * that makes links, the account invites too (both need the session), so Share is always the people's.
 */
function openShare(shell: Shell): void {
  shell.ui.setDialog({ kind: 'invite', person: null });
}

/** The library's state for the save state's dot: typing not saved yet and saving share the quiet one. */
function savedState(status: AutosaveStatus): 'saved' | 'saving' | 'error' {
  switch (status) {
    case 'saved':
    case 'closed':
      return 'saved';
    case 'unsaved':
    case 'saving':
      return 'saving';
    case 'failed':
      return 'error';
  }
}

/**
 * The save state in words, with a dot beside them that is never alone (decision 3; WT4; the library's SavedIndicator,
 * not a live region: the workspace's own announcer says it once, A13). Fix-next 43: on a narrow screen the short words,
 * beside the book's title; the full ones are what a screen reader reads and the tooltip shows. Nothing with no chapter
 * open (no words, so no dot).
 */
function Saved(shell: Shell, status: () => string, short: () => string): Child {
  return whenSome(shell.workspace.status, (now) =>
    component(SavedIndicator, {
      live: false,
      get state() {
        return savedState(now());
      },
      get class() {
        return `save-status ${now()}`;
      },
      get label() {
        return status();
      },
      get short() {
        return shell.mode() === 'narrow' ? short() : undefined;
      },
    }),
  );
}

/** The writer's bar (WT1–WT15). */
export function WriterBar(shell: Shell, status: () => string, short: () => string): Child {
  const { t, workspace, ui } = shell;
  const wide = (): boolean => shell.mode() === 'wide';
  const formatting = (): boolean => ui.route() === 'format';
  const listLabel = (): string => (ui.listOpen() ? t('toolbar.hideChapters') : t('toolbar.showChapters'));
  return el(
    'div',
    { class: 'toolbar wr-bar' },
    // Part 11d, R3: back to the home page; Back in the browser does the same. On a narrow screen the mark alone, named.
    when(
      wide,
      () => component(Button, { variant: 'ghost', size: 'sm', iconStart: 'back', class: 'go-home', onClick: () => { shell.go('home'); }, children: t('home.library') }),
      () => component(IconButton, { icon: 'back', label: t('home.library'), class: 'go-home', onClick: () => { shell.go('home'); } }),
    ),
    // WT1: on a narrow screen the chapters open as a drawer from here.
    when(
      () => !wide(),
      () =>
        component(IconButton, {
          icon: 'panel',
          class: 'toggle-list',
          get label() {
            return listLabel();
          },
          get 'aria-expanded'() {
            return ui.listOpen() ? 'true' : 'false';
          },
          onClick: () => ui.setListOpen(!ui.listOpen()),
        }),
    ),
    // Decision 78: inside a book, its two pages.
    component(SegmentedControl, {
      label: t('format.pages'),
      size: 'sm',
      class: 'book-pages',
      get fullWidth() {
        return !wide();
      },
      options: [
        { value: 'write', label: t('format.writing') },
        { value: 'format', label: t('format.title') },
      ],
      get value() {
        return ui.route() === 'format' ? 'format' : 'write';
      },
      onChange: (page: string) => {
        shell.go(page === 'format' ? 'format' : 'write');
      },
    }),
    el(
      'div',
      { class: 'wr-crumbs' },
      el('span', { class: 'wr-crumbs__book' }, () => {
        const book = workspace.book();
        return book === null ? '' : shownBook(shell, book);
      }),
      el('span', { class: 'wr-crumbs__sep', 'aria-hidden': 'true' }, t('writer.crumbSep')),
      el('span', { class: 'wr-crumbs__chapter chapter-title' }, () => chapterLine(shell)),
    ),
    el(
      'div',
      { class: 'wr-bar__tools' },
      // Part 36a, RO: no save state while the account doesn't let the books change (nothing is typed then).
      when(
        () => shell.account.permit(),
        () => Saved(shell, status, short),
      ),
      // Part 19e, D′1 (cockatrice-ui's notes screen): with the notes open, the bar holds whether the writer's notes are marked
      // in the text in place of the words and the layout, which the open panel sets aside.
      when(
        () => wide() && !ui.notesOpen() && !formatting(),
        () => [WordsButton(shell), Rule(), LayoutSwitch(shell)],
        () => when(() => wide() && !formatting(), () => [NoteMarksToggle(shell), Rule()]),
      ),
      when(wide, () => [
        // Decision 78: Find, Notes and History work on the text, which Formatting doesn't show.
        when(() => !formatting(), () => [FindButton(shell), NotesButton(shell), LaterIcon(shell, 'history', t('writer.history')), Rule()]),
        when(
          () => shell.sharing.invitesAvailable(),
          () => component(Button, { size: 'sm', class: 'open-share', 'aria-haspopup': 'dialog', onClick: () => { openShare(shell); }, children: t('writer.share') }),
          () => component(Button, { size: 'sm', class: 'share-later', 'aria-disabled': 'true', children: [t('writer.share'), el('small', {}, t('home.later'))] }),
        ),
      ]),
      // On a narrow screen More comes before Export, which ends the bar as the design's phone bar has it (WT13).
      when(() => !wide(), () => MoreMenu(shell)),
      // Part 36a, RO: the design's "Read only" chip, just left of Export.
      when(
        () => !shell.account.permit(),
        () => component(Badge, { icon: 'lock', class: 'read-only-chip', children: shell.t('account.readOnly') }),
      ),
      ExportMenu(shell, { class: 'writer-export', variant: 'primary', size: 'sm', label: t('home.export'), chevron: wide }),
      when(wide, () => MoreMenu(shell)),
    ),
  );
}

/** WL4: on a narrow screen, the bottom switch between the text and the pages (Y8, Y9): the library's BottomBar. */
export function ViewTabs(shell: Shell): Child {
  const { t, ui } = shell;
  return component(BottomBar, {
    label: t('writer.view'),
    class: 'wr-tabs',
    // The workspace's grid gives it its row; the layout, not the window's width, decides when it shows.
    position: 'static',
    always: true,
    children: component(SegmentedControl, {
      label: t('writer.view'),
      fullWidth: true,
      options: [
        { value: 'text', label: t('writer.text') },
        { value: 'preview', label: t('writer.pages') },
      ],
      get value() {
        return ui.narrowPane();
      },
      onChange: (pane: string) => {
        ui.setNarrowPane(pane === 'preview' ? 'preview' : 'text');
      },
    }),
  });
}

/** WT14: the chapter list's foot, in the rail and in the narrow screen's drawer: the book's words, once known. */
export function RailFoot(shell: Shell): Child {
  const { t, workspace } = shell;
  const words = (): number | null => {
    const bookId = workspace.openBookId();
    return bookId === null ? null : workspace.writing(bookId).words;
  };
  return whenSome(words, (count) => el('p', { class: 'wr-rail__foot' }, () => t('writer.railFoot', { count: count() })));
}

/**
 * The rail's chapters for the library's ChapterList: one object per chapter ID for as long as it is listed (so its row,
 * and the focus on it, stays as the listing is read again), its number and title read where they show.
 */
function railChapters(shell: Shell): () => readonly Chapter[] {
  const { workspace } = shell;
  interface Place {
    readonly number: number;
    readonly title: string;
  }
  let kept = new Map<string, { readonly chapter: Chapter; readonly move: (place: Place) => void }>();
  return createMemo(() => {
    const next = new Map<string, { readonly chapter: Chapter; readonly move: (place: Place) => void }>();
    for (const [index, entry] of (workspace.listing()?.chapters ?? []).entries()) {
      const place = { number: index + 1, title: shownTitle(shell, entry) };
      const row = kept.get(entry.id);
      if (row === undefined) {
        // Untracked: a row's place is its own signal, set as the listing changes, never a reason to make the list again.
        const [now, move] = untrack(() => createSignal<Place>(place, { equals: (a, b) => a.number === b.number && a.title === b.title }));
        next.set(entry.id, {
          move,
          chapter: {
            id: entry.id,
            get number() {
              return now().number;
            },
            get title() {
              return now().title;
            },
          },
        });
      } else {
        row.move(place);
        next.set(entry.id, row);
      }
    }
    kept = next;
    return [...next.values()].map((row) => row.chapter);
  });
}

/**
 * WT1, WT14: the chapter rail on a wide screen: its toggle, then the chapters' numbers (the library's collapsed
 * ChapterList, each a button opening its chapter) or, open, the whole chapter list, and the book's words at its foot.
 */
export function ChapterRail(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  const chapters = railChapters(shell);
  return el(
    'div',
    { class: () => (ui.listOpen() ? 'wr-rail list-pane sidebar' : 'wr-rail'), 'data-open': () => (ui.listOpen() ? 'true' : 'false') },
    component(Button, {
      variant: 'ghost',
      size: 'sm',
      iconStart: 'panel',
      class: 'wr-rail__toggle toggle-list',
      get 'aria-expanded'() {
        return ui.listOpen() ? 'true' : 'false';
      },
      get 'aria-label'() {
        return ui.listOpen() ? t('toolbar.hideChapters') : t('toolbar.showChapters');
      },
      onClick: () => ui.setListOpen(!ui.listOpen()),
      children: when(ui.listOpen, () => el('span', { class: 'wr-rail__label', 'aria-hidden': 'true' }, t('writer.chapters'))),
    }),
    when(
      ui.listOpen,
      () => ChapterList(shell, RailFoot(shell)),
      () =>
        component(UiChapterList, {
          class: 'wr-rail__numbers',
          label: t('nav.label'),
          collapsed: true,
          button: true,
          // Each number named by the chapter's title alone, as before the library (an imported "Chapter 3" would read
          // "Chapter 3: Chapter 3" the library's way).
          labelFor: (chapter: Chapter) => chapter.title,
          get chapters() {
            return chapters();
          },
          get currentId() {
            return workspace.chapterId() ?? undefined;
          },
          get disabled() {
            return !workspace.editable();
          },
          onSelect: (chapter: Chapter) => void workspace.openChapterById(String(chapter.id)).then(shell.focusEditor),
        }),
    ),
  );
}
